// addAppSidebar.js — THE BUILDER'S SIDEBARS (pages/add-app.js; Nika 2026-10-23:
// "make sidebar more appealing and beautiful"). The house rail parts carry the
// look — AppSidebarGroup's eyebrow, the kit's segmented choice, AppSidebarRow's
// one row law (components/AddAppSidebarRow.js) — and these say WHAT shows:
//   addAppSidebarRows   the person's apps where they live (Private · Shared),
//                       as rail rows: the glyph, the name, when it last changed
//   addAppSidebarEmpty  the line for a place with no app yet
//   addAppScopeSelect   the choice of the place (AppSegmented `select`)
//   addAppSidebarPick   a row's press: that app's thread and build
//   addAppCurrentName   the conversation's head: the app being built, or a new
//                       one
// One builder, two places: /add-app and the home's Build tab (functions/
// homeBuild.js) mount the same page, so both read these.
//
// FRANK: every export is self-contained; siblings through `this.call`.

// The person's apps in the chosen place, newest first, as AppSidebarRow's state
// (`label`, `icon`, `meta`); `id` is the app's own. Keys are prefixed, so no row
// is keyed by an HTML tag name. Every key is read up front (reactive callers).
export const addAppSidebarRows = function addAppSidebarRows(root) {
  const r = root && typeof root === 'object' ? root : {}
  const scope = r._addAppScope === 'shared' ? 'shared' : 'private'
  const apps = Array.isArray(r._userApps) ? r._userApps : []
  const fns = (this.context && this.context.functions) || {}
  const word = (key) => {
    const v = this.call('polyglot', key)
    return v == null ? '' : String(v)
  }
  const safe = (v) => String(v == null ? '' : v).replace(/[^A-Za-z0-9]+/g, '')
  const at = (ext) => {
    const raw = ext.updatedAt || ext.updated_at || ext.createdAt || ext.created_at
    const t = typeof raw === 'number' ? raw : Date.parse(raw || '')
    return Number.isFinite(t) ? t : 0
  }
  return apps
    .filter((ext) => ext && ext.id && (ext.scope || 'private') === scope)
    .map((ext, i) => ({ ext, t: at(ext), i }))
    .sort((a, b) => b.t - a.t || a.i - b.i)
    .map(({ ext, t }) => ({
      key: 'addApp' + safe(ext.id),
      id: String(ext.id),
      label: String(ext.name || '').trim() || word('workspace.addApp.extensionFallback'),
      icon: ext.appKind === 'multipage' ? 'layers' : 'blocks',
      meta: t && typeof fns.timeAgo === 'function' ? String(this.call('timeAgo', t) || '') : ''
    }))
}

// The line for a place that holds no app yet — how one gets there.
export const addAppSidebarEmpty = function addAppSidebarEmpty(root) {
  const shared = !!root && root._addAppScope === 'shared'
  return shared
    ? { title: 'workspace.addApp.apps.emptyShared', line: 'workspace.addApp.apps.emptySharedLine' }
    : {
        title: 'workspace.addApp.apps.emptyPrivate',
        line: 'workspace.addApp.apps.emptyPrivateLine'
      }
}

// The place's choice — the kit segmented control calls it with the segment's
// value (`select: 'addAppScopeSelect'`).
export const addAppScopeSelect = function addAppScopeSelect(value) {
  const root = this.state && this.state.root
  if (!root || typeof root.update !== 'function') return false
  if (value !== 'private' && value !== 'shared') return false
  if ((root._addAppScope === 'shared' ? 'shared' : 'private') === value) return false
  root.update({ _addAppScope: value })
  return true
}

// A row's press: the app's own thread and its build in the canvas
// (functions/index.js selectAddAppExtension — the full record it needs).
export const addAppSidebarPick = function addAppSidebarPick(id) {
  const root = this.state && this.state.root
  const apps = (root && Array.isArray(root._userApps) && root._userApps) || []
  const ext = apps.find((a) => a && String(a.id) === String(id))
  if (!ext) return false
  const self = this
  // A phone shows one pane at a time: the picked app's preview
  // (functions/addAppPhonePane.js — the pane falls back to the chat until
  // something renders there).
  const fns0 = (this.context && this.context.functions) || {}
  if (typeof fns0.addAppPhonePaneSet === 'function') this.call('addAppPhonePaneSet', 'preview')
  try {
    // Where the panes stack (a phone), the app comes into view once it is
    // picked — the canvas sits under the conversation there (persona round
    // 3, W2: marcus tapped his app and nothing changed on screen).
    Promise.resolve(this.call('selectAddAppExtension', ext))
      .then(() => {
        const fns = (self.context && self.context.functions) || {}
        if (typeof fns.addAppShowCanvas === 'function') self.call('addAppShowCanvas', self)
      })
      .catch((err) => console.warn('[add-app] select extension failed', err))
  } catch (err) {
    console.warn('[add-app] select extension failed', err)
    return false
  }
  return true
}

// The conversation's head: the name of the app being built (bound to the
// thread), or "New app" before there is one.
export const addAppCurrentName = function addAppCurrentName(root) {
  const r = root && typeof root === 'object' ? root : {}
  const id = r._addAppProjectId ? String(r._addAppProjectId) : ''
  const apps = Array.isArray(r._userApps) ? r._userApps : []
  const ext = id ? apps.find((a) => a && String(a.id) === id) : null
  const name = ext && String(ext.name || '').trim()
  if (name) return name
  const v = this.call(
    'polyglot',
    id ? 'workspace.addApp.extensionFallback' : 'workspace.addApp.chat.newApp'
  )
  return v == null ? '' : String(v)
}
